import SvgCanvas from '@svgedit/svgcanvas'
import LayersPanelHtml from './LayersPanel.html'
import { getTreeChildren, getNodeLabel, getNodeLayer, isTreeNodeLocked, moveTreeElements, deleteTreeElements } from '../utils/layer-tree.js'

const { $id, $click, NS } = SvgCanvas

class LayersPanel {
  constructor (editor) {
    this.updateContextPanel = editor.topPanel.updateContextPanel.bind(editor.topPanel)
    this.editor = editor
    this.collapsed = new WeakSet()
    this.rows = new Map()
    this.refreshFrame = 0
    this.selecting = false
  }

  lmenuFunc (e) {
    switch (e?.detail?.trigger) {
      case 'dupe': this.cloneLayer(); break
      case 'delete': this.deleteLayer(); break
      case 'merge_down': this.mergeLayer(); break
      case 'merge_all':
        this.editor.svgCanvas.mergeAllLayers()
        this.updateContextPanel()
        this.populateLayers()
        break
    }
  }

  init () {
    const template = document.createElement('template')
    const { i18next } = this.editor
    template.innerHTML = LayersPanelHtml
    this.editor.$svgEditor.append(template.content.cloneNode(true))
    const menuMore = document.createElement('se-cmenu-layers')
    menuMore.id = 'se-cmenu-layers-more'
    menuMore.value = 'layer_moreopts'
    menuMore.setAttribute('leftclick', true)
    this.editor.$container.append(menuMore)
    menuMore.init(i18next)
    $click($id('layer_new'), this.newLayer.bind(this))
    $click($id('layer_delete'), this.deleteSelection.bind(this))
    $click($id('layer_up'), () => this.moveLayer(-1))
    $click($id('layer_down'), () => this.moveLayer(1))
    $click($id('layer_rename'), this.layerRename.bind(this))
    menuMore.addEventListener('change', this.lmenuFunc.bind(this))
    $click($id('sidepanel_handle'), () => this.toggleSidePanel())
    this.toggleSidePanel(this.editor.configObj.curConfig.showlayers)
    const list = $id('layerlist')
    list.setAttribute('aria-label', i18next.t('layers.layers'))
    list.addEventListener('click', e => this.onTreeClick(e))
    list.addEventListener('contextmenu', e => {
      const row = e.target.closest('tr[data-node-id]')
      if (!row) return
      e.preventDefault()
      const element = row.svgElement
      if (this.editor.svgCanvas.isLayer(element)) {
        this.selectElement(element)
        if (this.editor.svgCanvas.getCurrentDrawing().getCurrentLayer() === element) menuMore.openAt(e.clientX, e.clientY)
        return
      }
      if (!this.editor.svgCanvas.getSelectedElements().includes(element)) this.selectElement(element)
      if (!this.editor.svgCanvas.getSelectedElements().includes(element)) return
      $id('se-cmenu_canvas').openAt(e.clientX, e.clientY)
    })
    list.addEventListener('keydown', e => this.onTreeKey(e))
    list.addEventListener('pointerdown', e => this.beginDrag(e))
    list.addEventListener('pointermove', e => this.updateDrag(e))
    list.addEventListener('pointerup', e => this.endDrag(e))
    list.addEventListener('pointercancel', () => this.cancelDrag())
    const dialog = $id('layer_move_dialog')
    $id('layer_move_title').textContent = i18next.t('layers.move_to')
    $id('layer_move_destination_label').textContent = i18next.t('layers.destination')
    $id('layer_move_name_label').textContent = i18next.t('layers.new_name')
    $id('layer_move_confirm').textContent = i18next.t('common.ok')
    $id('layer_move_cancel').textContent = i18next.t('common.cancel')
    $id('layer_move_destination').addEventListener('change', () => {
      $id('layer_move_name_wrap').hidden = $id('layer_move_destination').value !== '__new__'
    })
    $id('layer_move_cancel').addEventListener('click', () => dialog.close())
    dialog.addEventListener('close', () => { this.moveSelection = null })
    $id('layer_move_form').addEventListener('submit', e => {
      e.preventDefault()
      const select = $id('layer_move_destination')
      const name = $id('layer_move_name').value.trim()
      const drawing = this.editor.svgCanvas.getCurrentDrawing()
      const isNew = select.value === '__new__'
      if (isNew && (!name || drawing.hasLayer(name))) {
        $id('layer_move_error').textContent = i18next.t('layers.unique_name')
        return
      }
      const destination = isNew ? null : this.moveDestinations[Number(select.value)]
      if (this.moveElements(this.moveSelection, destination, null, isNew ? { newLayerName: name } : {}, $id('layer_move_error'))) dialog.close()
    })
  }

  toggleSidePanel (displayFlag) {
    this.editor.$svgEditor.classList.toggle('open', displayFlag ?? !this.editor.$svgEditor.classList.contains('open'))
  }

  newLayer () {
    const drawing = this.editor.svgCanvas.getCurrentDrawing()
    let i = drawing.getNumLayers()
    let name
    do { name = this.editor.i18next.t('layers.layer') + ' ' + ++i } while (drawing.hasLayer(name))
    const newName = prompt(this.editor.i18next.t('notification.enterUniqueLayerName'), name)
    if (!newName) return
    if (drawing.hasLayer(newName)) {
      alert(this.editor.i18next.t('notification.dupeLayerName'))
      return
    }
    this.editor.svgCanvas.createLayer(newName)
    this.updateContextPanel()
    this.populateLayers()
  }

  deleteSelection () {
    const selected = this.editor.svgCanvas.getSelectedElements().filter(Boolean)
    if (!selected.length) return this.deleteLayer()
    try {
      deleteTreeElements(this.editor.svgCanvas, selected)
      this.populateLayers()
    } catch (error) { this.showError(error) }
  }

  deleteLayer () {
    if (this.editor.svgCanvas.deleteCurrentLayer()) {
      this.updateContextPanel()
      this.populateLayers()
    }
  }

  cloneLayer () {
    const drawing = this.editor.svgCanvas.getCurrentDrawing()
    const name = prompt(this.editor.i18next.t('notification.enterUniqueLayerName'), drawing.getCurrentLayerName() + ' copy')
    if (!name) return
    if (drawing.hasLayer(name)) {
      alert(this.editor.i18next.t('notification.dupeLayerName'))
      return
    }
    this.editor.svgCanvas.cloneLayer(name)
    this.updateContextPanel()
    this.populateLayers()
  }

  mergeLayer () {
    if (this.editor.svgCanvas.indexCurrentLayer() <= 0) return
    this.editor.svgCanvas.mergeLayer()
    this.updateContextPanel()
    this.populateLayers()
  }

  moveLayer (pos) {
    const index = this.editor.svgCanvas.indexCurrentLayer()
    if (index !== -1) {
      this.editor.svgCanvas.setCurrentLayerPosition(index - pos)
      this.populateLayers()
    }
  }

  layerRename () {
    const drawing = this.editor.svgCanvas.getCurrentDrawing()
    const name = prompt(this.editor.i18next.t('notification.enterNewLayerName'), '')
    if (!name) return
    if (drawing.hasLayer(name)) {
      alert(this.editor.i18next.t('notification.layerHasThatName'))
      return
    }
    this.editor.svgCanvas.renameCurrentLayer(name)
    this.populateLayers()
  }

  scheduleRefresh () {
    if (this.refreshFrame) return
    this.refreshFrame = requestAnimationFrame(() => {
      this.refreshFrame = 0
      this.populateLayers()
    })
  }

  populateLayers () {
    const list = $id('layerlist')?.querySelector('tbody')
    if (!list) return
    const canvas = this.editor.svgCanvas
    const drawing = canvas.getCurrentDrawing()
    const fragment = document.createDocumentFragment()
    this.rows.clear()
    const names = []
    const append = (element, depth, layerName) => {
      const row = document.createElement('tr')
      row.className = layerName === undefined ? 'tree-object' : 'layer'
      row.dataset.nodeId = element.id
      row.svgElement = element
      row.setAttribute('role', 'treeitem')
      row.setAttribute('aria-label', layerName ?? getNodeLabel(element))
      row.setAttribute('aria-level', depth + 1)
      row.tabIndex = -1
      const children = getTreeChildren(element)
      if (children.length || layerName !== undefined) row.setAttribute('aria-expanded', !this.collapsed.has(element))
      const visible = element.style.display !== 'none' && element.getAttribute('display') !== 'none'
      const locked = isTreeNodeLocked(element, canvas.getSvgContent())
      const control = (kind, icon, label) => {
        const cell = document.createElement('td')
        cell.className = kind
        const button = document.createElement('button')
        button.type = 'button'
        button.dataset.action = kind
        button.setAttribute('aria-label', label)
        const img = document.createElement('img')
        img.src = `./images/${icon}.svg`
        img.alt = ''
        button.append(img)
        cell.append(button)
        row.append(cell)
        return cell
      }
      control('layervis', 'eye', this.editor.i18next.t('layers.visibility')).classList.toggle('layerinvis', !visible)
      control('layerlock', locked ? 'lock' : 'unlock', this.editor.i18next.t('layers.lock'))
      const cell = document.createElement('td')
      cell.className = 'layername'
      cell.style.paddingInlineStart = `${depth * 14}px`
      if (children.length || layerName !== undefined) {
        const toggle = document.createElement('button')
        toggle.type = 'button'
        toggle.className = 'tree-toggle'
        toggle.dataset.action = 'toggle'
        toggle.textContent = this.collapsed.has(element) ? '▸' : '▾'
        toggle.setAttribute('aria-label', this.editor.i18next.t(this.collapsed.has(element) ? 'layers.expand' : 'layers.collapse'))
        cell.append(toggle)
      }
      const label = document.createElement('span')
      label.className = 'tree-label'
      label.textContent = layerName ?? getNodeLabel(element)
      cell.append(label)
      row.append(cell)
      fragment.append(row)
      this.rows.set(element, row)
      if (!this.collapsed.has(element)) children.forEach(child => append(child, depth + 1))
    }
    for (let i = drawing.getNumLayers() - 1; i >= 0; i--) {
      const name = drawing.getLayerName(i)
      names.push(name)
      append(drawing.getLayerByName(name), 0, name)
    }
    list.replaceChildren(fragment)
    const select = $id('selLayerNames')
    select.replaceChildren(...names.map(name => new Option(name, name)))
    select.value = drawing.getCurrentLayerName()
    this.syncSelection(false)
    canvas.runExtensions('layersChanged')
  }

  syncSelection (reveal = true) {
    if (this.selecting || !$id('layerlist')) return
    const canvas = this.editor.svgCanvas
    const selected = new Set(canvas.getSelectedElements().filter(Boolean))
    let expanded = false
    if (reveal) {
      for (const element of selected) {
        for (let parent = element.parentElement; parent && parent !== canvas.getSvgContent(); parent = parent.parentElement) {
          if (this.collapsed.delete(parent)) expanded = true
        }
      }
    }
    if (expanded) this.populateLayers()
    const drawing = canvas.getCurrentDrawing()
    const layer = drawing.getLayerByName(drawing.getCurrentLayerName())
    let focusRow
    for (const [element, row] of this.rows) {
      const active = selected.has(element) || (!selected.size && element === layer)
      row.classList.toggle('layersel', active || element === layer)
      row.setAttribute('aria-selected', active)
      row.tabIndex = active && !focusRow ? 0 : -1
      if (active) focusRow = row
    }
    $id('selLayerNames').disabled = !selected.size
    if (reveal && selected.size) focusRow?.scrollIntoView({ block: 'nearest' })
  }

  selectElement (element, additive = false) {
    const canvas = this.editor.svgCanvas
    if (isTreeNodeLocked(element, canvas.getSvgContent())) {
      this.showError(new Error(this.editor.i18next.t('layers.unavailable')))
      return
    }
    const layer = getNodeLayer(canvas, element)
    if (!layer) return
    for (let node = element; node && node !== canvas.getSvgContent(); node = node.parentElement) {
      if (node.style.display === 'none' || node.getAttribute('display') === 'none' || getComputedStyle(node).visibility === 'hidden') {
        this.showError(new Error(this.editor.i18next.t('layers.unavailable')))
        return
      }
    }
    const drawing = canvas.getCurrentDrawing()
    const selected = canvas.getSelectedElements().filter(Boolean)
    this.selecting = true
    try {
      canvas.leaveContext()
      for (let i = 0; i < drawing.getNumLayers(); i++) {
        const name = drawing.getLayerName(i)
        if (drawing.getLayerByName(name) === layer) { canvas.setCurrentLayer(name); break }
      }
      if (element !== layer) {
        if (element.parentNode !== layer) canvas.setContext(element.parentNode)
        const siblings = additive ? selected.filter(el => el.parentNode === element.parentNode && el !== element) : []
        const next = additive && selected.includes(element) ? siblings : [...siblings, element]
        canvas.selectOnly(next)
      }
    } finally { this.selecting = false }
    this.syncSelection()
  }

  onTreeClick (e) {
    if (this.ignoreClick) return
    const row = e.target.closest('tr[data-node-id]')
    if (!row) return
    const element = row.svgElement
    const action = e.target.closest('button')?.dataset.action
    if (action === 'toggle') {
      if (!this.collapsed.delete(element)) this.collapsed.add(element)
      this.populateLayers()
    } else if (action === 'layervis' || action === 'layerlock') {
      const canvas = this.editor.svgCanvas
      canvas.leaveContext()
      const old = { style: element.getAttribute('style'), transform: element.getAttribute('transform') }
      const oldLock = element.getAttributeNS(NS.SODIPODI, 'insensitive')
      if (action === 'layervis') {
        old.display = element.getAttribute('display')
        const hidden = element.style.display === 'none' || old.display === 'none'
        element.removeAttribute('display')
        element.style.display = hidden ? '' : 'none'
        canvas.runExtensions('layerVisChanged')
      } else {
        if (oldLock === 'true') element.removeAttributeNS(NS.SODIPODI, 'insensitive')
        else element.setAttributeNS(NS.SODIPODI, 'sodipodi:insensitive', 'true')
        element.style.pointerEvents = oldLock === 'true' ? 'inherit' : 'none'
      }
      canvas.clearSelection()
      const command = new canvas.history.ChangeElementCommand(element, old)
      if (element.localName === 'text') command.type = () => 'TreeTextStyleCommand'
      if (action === 'layerlock') {
        const newLock = element.getAttributeNS(NS.SODIPODI, 'insensitive')
        for (const [method, value] of [['apply', newLock], ['unapply', oldLock]]) {
          const original = command[method].bind(command)
          command[method] = handler => {
            if (value === null) element.removeAttributeNS(NS.SODIPODI, 'insensitive')
            else element.setAttributeNS(NS.SODIPODI, 'sodipodi:insensitive', value)
            original(handler)
          }
        }
      }
      canvas.addCommandToHistory(command)
      canvas.call('changed', [element])
      this.populateLayers()
    } else {
      this.selectElement(element, e.shiftKey || e.ctrlKey || e.metaKey)
      this.rows.get(element)?.focus({ preventScroll: true })
    }
  }

  onTreeKey (e) {
    const row = e.target.closest('tr[data-node-id]')
    if (!row || e.target.closest('button')) return
    if (['Enter', ' ', 'Delete', 'Backspace', 'ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)) e.stopPropagation()
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      this.selectElement(row.svgElement, e.shiftKey)
      row.focus({ preventScroll: true })
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault()
      this.deleteSelection()
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      ;(e.key === 'ArrowDown' ? row.nextElementSibling : row.previousElementSibling)?.focus()
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault()
      if (e.key === 'ArrowLeft') this.collapsed.add(row.svgElement)
      else this.collapsed.delete(row.svgElement)
      this.populateLayers()
      this.rows.get(row.svgElement)?.focus()
    }
  }

  beginDrag (e) {
    const row = e.target.closest('tr[data-node-id]')
    if (e.button !== 0 || !row || e.target.closest('button') || this.editor.svgCanvas.isLayer(row.svgElement)) return
    const selected = this.editor.svgCanvas.getSelectedElements().filter(Boolean)
    this.drag = { x: e.clientX, y: e.clientY, pointerId: e.pointerId, elements: selected.includes(row.svgElement) ? selected : [row.svgElement], active: false }
  }

  updateDrag (e) {
    if (!this.drag) return
    if (Math.hypot(e.clientX - this.drag.x, e.clientY - this.drag.y) < 5 && !this.drag.active) return
    if (!this.drag.active) $id('layerlist').setPointerCapture(e.pointerId)
    this.drag.active = true
    e.preventDefault()
    const scroll = $id('layer_tree_scroll')
    const bounds = scroll.getBoundingClientRect()
    if (e.clientY < bounds.top + 20) scroll.scrollTop -= 16
    if (e.clientY > bounds.bottom - 20) scroll.scrollTop += 16
    this.dropRow?.classList.remove('drop-before', 'drop-after', 'drop-inside')
    const row = document.elementFromPoint(e.clientX, e.clientY)?.closest('#layerlist tr[data-node-id]')
    this.dropRow = row
    this.dropTarget = null
    if (!row) return
    const element = row.svgElement
    const rect = row.getBoundingClientRect()
    const fraction = (e.clientY - rect.top) / rect.height
    const inside = this.editor.svgCanvas.isLayer(element) || (element.localName === 'g' && fraction > 0.25 && fraction < 0.75)
    row.classList.add(inside ? 'drop-inside' : fraction < 0.5 ? 'drop-before' : 'drop-after')
    this.dropTarget = { parent: inside ? element : element.parentNode, before: inside ? null : fraction < 0.5 ? element.nextSibling : element }
  }

  endDrag (e) {
    if (!this.drag) return
    const { active, elements } = this.drag
    const target = this.dropTarget
    this.cancelDrag()
    if (!active) return
    e.preventDefault()
    this.ignoreClick = true
    setTimeout(() => { this.ignoreClick = false }, 0)
    if (target) this.moveElements(elements, target.parent, target.before)
  }

  cancelDrag () {
    if (this.drag && $id('layerlist').hasPointerCapture(this.drag.pointerId)) $id('layerlist').releasePointerCapture(this.drag.pointerId)
    this.dropRow?.classList.remove('drop-before', 'drop-after', 'drop-inside')
    this.drag = null
    this.dropTarget = null
    this.dropRow = null
  }

  moveElements (elements, destination, before = null, options = {}, errorTarget) {
    try {
      const canvas = this.editor.svgCanvas
      const moved = moveTreeElements(canvas, elements ?? [], destination, before, options)
      if (moved.length) {
        const parent = moved[0].parentNode
        this.collapsed.delete(parent)
        if (!canvas.isLayer(parent)) canvas.setContext(parent)
        canvas.selectOnly(moved)
      }
      this.populateLayers()
      this.syncSelection()
      $id('layer_tree_error').textContent = ''
      return true
    } catch (error) {
      this.showError(error, errorTarget)
      return false
    }
  }

  moveSelectedToLayer (name) {
    const canvas = this.editor.svgCanvas
    return this.moveElements(canvas.getSelectedElements().filter(Boolean), canvas.getCurrentDrawing().getLayerByName(name))
  }

  showError (error, target = $id('layer_tree_error')) {
    console.warn('LayersPanel:', error)
    target.textContent = this.editor.i18next.t(`layers.errors.${error.code}`, { defaultValue: error.message })
  }

  openMoveDialog () {
    const canvas = this.editor.svgCanvas
    this.moveSelection = canvas.getSelectedElements().filter(Boolean)
    if (!this.moveSelection.length) return
    const drawing = canvas.getCurrentDrawing()
    this.moveDestinations = []
    const options = []
    for (let i = 0; i < drawing.getNumLayers(); i++) {
      const name = drawing.getLayerName(i)
      const layer = drawing.getLayerByName(name)
      this.moveDestinations.push(layer)
      const option = new Option(name, String(i))
      option.disabled = isTreeNodeLocked(layer, canvas.getSvgContent()) || layer.style.display === 'none' || layer.getAttribute('display') === 'none'
      options.push(option)
    }
    options.push(new Option(this.editor.i18next.t('layers.new'), '__new__'))
    $id('layer_move_destination').replaceChildren(...options)
    $id('layer_move_name').value = ''
    $id('layer_move_name_wrap').hidden = true
    $id('layer_move_error').textContent = ''
    $id('layer_move_dialog').showModal()
  }
}

export default LayersPanel
